import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Avatar,
  Chip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Paper,
  InputAdornment,
  Autocomplete,
  Stack,
  Checkbox,
  LinearProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  Person,
  Payment,
  CheckCircle,
  CurrencyRupee,
  Close,
  AccountBalance,
  CreditCard,
  Money,
  PhoneAndroid,
  Speed,
  SelectAll,
  Deselect,
  Group,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { formatINR } from '../utils/currency';
import { StatusIndicator, MethodOptionContent, METHOD_VALUES, methodLabel } from './Indicators';
import { ACCENTS } from '../theme/accents';

const QuickCollection = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  // Phones stack search above the payment form, so the form is off-screen
  // after picking a member unless we scroll to it
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const detailsRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'UPI',
    paymentDate: dayjs().format('YYYY-MM-DD'),
    paymentMonth: dayjs().format('YYYY-MM'),
    notes: '',
  });


  useEffect(() => {
    if (open) {
      fetchMembers();
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = members.filter(member =>
        member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phone?.includes(searchTerm)
      );
      setFilteredMembers(filtered);
    } else {
      setFilteredMembers(members);
    }
  }, [searchTerm, members]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get('/api/payments/quick-collection-members');
      setMembers(response.data);
      setFilteredMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch members');
    }
  };

  const resetForm = () => {
    setSearchTerm('');
    setSelectedMembers([]);
    setMultiSelectMode(false);
    setPaymentData({
      // Left blank - filled from the selected member's own due
      amount: '',
      paymentMethod: 'UPI',
      paymentDate: dayjs().format('YYYY-MM-DD'),
      paymentMonth: dayjs().format('YYYY-MM'),
      notes: '',
    });
  };

  const handleMemberToggle = (member) => {
    if (multiSelectMode) {
      setSelectedMembers(prev => {
        const isSelected = prev.some(m => m.id === member.id);
        if (isSelected) {
          return prev.filter(m => m.id !== member.id);
        } else {
          return [...prev, member];
        }
      });
    } else {
      setSelectedMembers([member]);
      setPaymentData(prev => ({
        ...prev,
        // Prefill this member's own due (base share x their multiplier)
        amount: amountFor(member),
        notes: `Quick collection from ${member.full_name}`,
      }));
      if (isPhone) {
        setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      }
    }
  };

  // Each member owes their own amount, so a flat figure cannot be assumed
  const amountFor = (member) => Number(member?.expected_amount ?? 0);

  const selectedTotal = selectedMembers.reduce(
    (sum, member) => sum + (multiSelectMode ? amountFor(member) : Number(paymentData.amount) || 0),
    0
  );

  const handleSelectAll = () => {
    const availableMembers = filteredMembers.filter(member => 
      member.payment_status !== 'completed'
    );
    setSelectedMembers(availableMembers);
  };

  const handleDeselectAll = () => {
    setSelectedMembers([]);
  };

  const handleQuickPayment = async () => {
    if (selectedMembers.length === 0) {
      toast.error('Please select at least one member');
      return;
    }

    // In multi-select each member is charged their own due, so the shared amount
    // field is only required for a single collection.
    if (!multiSelectMode && (!paymentData.amount || parseFloat(paymentData.amount) <= 0)) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (multiSelectMode && selectedMembers.length > 1) {
      setProcessing(true);
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      try {
        // Process payments in parallel batches of 5
        const batchSize = 5;
        for (let i = 0; i < selectedMembers.length; i += batchSize) {
          const batch = selectedMembers.slice(i, i + batchSize);
          
          await Promise.allSettled(
            batch.map(async (member) => {
              try {
                await axios.post('/api/payments', {
                  userId: member.id,
                  ...paymentData,
                  amount: amountFor(member),
                  notes: paymentData.notes || `Quick collection batch payment`,
                  status: 'completed',
                });
                successCount++;
              } catch (error) {
                failCount++;
                errors.push(`${member.full_name}: ${error.response?.data?.error || error.message}`);
              }
            })
          );
        }

        // Show results
        if (successCount > 0) {
          toast.success(`Successfully recorded ${successCount} payment${successCount > 1 ? 's' : ''}! 🎉`);
        }
        
        if (failCount > 0) {
          toast.error(`Failed to record ${failCount} payment${failCount > 1 ? 's' : ''}`);
          console.error('Payment creation errors:', errors);
        }

        if (successCount > 0) {
          onSuccess?.();
          onClose();
        }

      } catch (error) {
        console.error('Batch payment creation error:', error);
        toast.error('Failed to process payments');
      } finally {
        setProcessing(false);
      }
    } else {
      // Single payment mode
      setLoading(true);
      try {
        const member = selectedMembers[0];
        await axios.post('/api/payments', {
          userId: member.id,
          ...paymentData,
          status: 'completed',
        });

        toast.success(`${formatINR(paymentData.amount)} payment recorded for ${member.full_name}! 🎉`);
        onSuccess?.();
        onClose();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to record payment');
      } finally {
        setLoading(false);
      }
    }
  };

  const getMemberPaymentStatus = (member) => {
    return { 
      status: member.payment_status || 'pending', 
      lastPayment: member.last_payment_date,
      amount: member.last_payment_amount,
      method: member.last_payment_method,
    };
  };

  const titleIcon = multiSelectMode
    ? <Group sx={{ color: 'secondary.main' }} />
    : <Speed sx={{ color: 'secondary.main' }} />;

  const titleText = (
    <Typography variant="h6" fontWeight="bold" noWrap>
      {multiSelectMode ? 'Multi-Collection' : 'Quick Collection'}
    </Typography>
  );

  const modeSwitch = (
    <FormControlLabel
      control={
        <Switch
          checked={multiSelectMode}
          onChange={(e) => {
            setMultiSelectMode(e.target.checked);
            setSelectedMembers([]);
          }}
          color="secondary"
        />
      }
      label={
        <Typography variant="body2" color="textSecondary" noWrap>
          Multi-select
        </Typography>
      }
    />
  );

  const selectButtons = multiSelectMode && (
    <>
      <Button size="small" startIcon={<SelectAll />} onClick={handleSelectAll} disabled={processing}>
        {isPhone ? 'All' : 'All Available'}
      </Button>
      <Button size="small" startIcon={<Deselect />} onClick={handleDeselectAll} disabled={processing}>
        Clear
      </Button>
    </>
  );

  const closeButton = (
    <IconButton onClick={onClose} size="small" aria-label="close">
      <Close />
    </IconButton>
  );

  // Phone footer says less: the amount is what matters, and "Record Payment ₹0"
  // wrapped onto two lines
  const recordLabel = (() => {
    if (loading || processing) {
      return multiSelectMode && selectedMembers.length > 1 ? 'Processing...' : 'Recording...';
    }
    if (multiSelectMode && selectedMembers.length > 1) {
      return isPhone
        ? `Record ${selectedMembers.length} · ${formatINR(selectedTotal)}`
        : `Record ${selectedMembers.length} Payments (${formatINR(selectedTotal)})`;
    }
    if (isPhone) {
      return paymentData.amount ? `Record ${formatINR(paymentData.amount)}` : 'Record payment';
    }
    return `Record Payment ${formatINR(paymentData.amount)}`;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>
        {isPhone ? (
          // Two rows: title + close, then the mode switch with its actions.
          // On one row the title, switch label and buttons all wrapped.
          <>
            <Box display="flex" alignItems="center" gap={1.5}>
              {titleIcon}
              <Box sx={{ flex: 1, minWidth: 0 }}>{titleText}</Box>
              {closeButton}
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
              {modeSwitch}
              <Box display="flex" gap={0.5}>{selectButtons}</Box>
            </Box>
          </>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              {titleIcon}
              {titleText}
              {modeSwitch}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {selectButtons}
              {closeButton}
            </Box>
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Search Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 0, md: 2 } }}>
              <Typography variant="h6" gutterBottom>
                Search Members
              </Typography>
              <TextField
                fullWidth
                placeholder="Name, username or phone"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Members List - on a phone the dialog itself scrolls; a second
                  scroller inside it traps the thumb */}
              <Box sx={{ maxHeight: { xs: 'none', md: 400 }, overflowY: { xs: 'visible', md: 'auto' } }}>
                {filteredMembers.length > 0 ? (
                  <List>
                    {filteredMembers.map((member) => {
                      const paymentStatus = getMemberPaymentStatus(member);
                      const isSelected = selectedMembers.some(m => m.id === member.id);
                      const canSelect = paymentStatus.status !== 'completed';
                      
                      return (
                        <ListItem
                          key={member.id}
                          button
                          onClick={() => canSelect && handleMemberToggle(member)}
                          selected={isSelected}
                          disabled={!canSelect || processing}
                          sx={{
                            borderRadius: 1,
                            mb: 1,
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(217, 164, 65, 0.16)',
                              '&:hover': {
                                backgroundColor: 'rgba(217, 164, 65, 0.24)',
                              },
                            },
                          }}
                        >
                          {multiSelectMode && (
                            <ListItemIcon>
                              <Checkbox
                                checked={isSelected}
                                disabled={!canSelect || processing}
                                onChange={() => canSelect && handleMemberToggle(member)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </ListItemIcon>
                          )}
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {member.full_name.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              // Wraps instead of squeezing: in a single non-wrapping row the
                              // status label collapsed to its dot and the amount ran over it
                              <Box display="flex" alignItems="center" flexWrap="wrap" columnGap={1} rowGap={0.25}>
                                <Typography variant="body1" fontWeight="medium" noWrap sx={{ maxWidth: '100%' }}>
                                  {member.full_name}
                                </Typography>
                                <Box sx={{ flexShrink: 0 }}>
                                  <StatusIndicator status={paymentStatus.status} size="small" />
                                </Box>
                                {paymentStatus.status === 'completed' && paymentStatus.amount && (
                                  <Typography variant="body2" className="tabular" sx={{ color: ACCENTS.emerald, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {formatINR(paymentStatus.amount)}
                                  </Typography>
                                )}
                                {paymentStatus.status !== 'completed' && amountFor(member) > 0 && (
                                  <Typography variant="body2" className="tabular" sx={{ color: ACCENTS.marigold, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {formatINR(amountFor(member))} due
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Stack direction="column" spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2" color="textSecondary">
                                    @{member.username}
                                  </Typography>
                                  {member.phone && (
                                    <Typography variant="body2" color="textSecondary">
                                      • {member.phone}
                                    </Typography>
                                  )}
                                </Stack>
                                {paymentStatus.status === 'completed' && paymentStatus.lastPayment && (
                                  <Typography variant="caption" color="textSecondary">
                                    Last paid: {dayjs(paymentStatus.lastPayment).format('MMM D')} 
                                    {paymentStatus.method && ` via ${methodLabel(paymentStatus.method)}`}
                                  </Typography>
                                )}
                              </Stack>
                            }
                          />
                          {!multiSelectMode && (
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => canSelect && handleMemberToggle(member)}
                                color={isSelected ? 'secondary' : 'default'}
                                disabled={!canSelect}
                              >
                                <Person />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center" 
                    py={4}
                  >
                    <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" color="textSecondary">
                      {searchTerm ? 'No members found' : 'Start typing to search members'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Payment Details Section */}
          <Grid item xs={12} md={6}>
            <Paper ref={detailsRef} sx={{ p: { xs: 1.5, sm: 2 }, scrollMarginTop: 8 }}>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>

              {selectedMembers.length > 0 ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      {multiSelectMode && selectedMembers.length > 1 ? (
                        <>
                          Recording payments for <strong>{selectedMembers.length} members</strong>
                          <br />
                          Total amount: <strong>{formatINR(selectedTotal)}</strong>
                        </>
                      ) : (
                        <>
                          Recording payment for <strong>{selectedMembers[0]?.full_name}</strong>
                        </>
                      )}
                    </Typography>
                  </Alert>
                  
                  {multiSelectMode && selectedMembers.length > 1 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Selected members:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {selectedMembers.map((member) => (
                          <Chip
                            key={member.id}
                            label={member.full_name}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onDelete={() => handleMemberToggle(member)}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={paymentData.amount}
                        onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        inputProps={{ inputMode: 'decimal' }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Method</InputLabel>
                        <Select
                          value={paymentData.paymentMethod}
                          label="Payment Method"
                          onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                        >
                          {METHOD_VALUES.map((method) => (
                            <MenuItem key={method} value={method}>
                              <MethodOptionContent method={method} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Payment Date"
                        type="date"
                        value={paymentData.paymentDate}
                        onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Payment Month"
                        value={paymentData.paymentMonth}
                        onChange={(e) => setPaymentData({ ...paymentData, paymentMonth: e.target.value })}
                        placeholder="YYYY-MM"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Notes"
                        value={paymentData.notes}
                        onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                        placeholder="Additional notes about this payment..."
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center" 
                  py={6}
                >
                  <CurrencyRupee sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Select a Member
                  </Typography>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    Choose a member from the search results to record their payment quickly
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: { xs: '12px 16px', sm: 3 } }}>
        {(processing || loading) && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" align="center" mt={1}>
              {processing ? 'Processing payments...' : 'Recording payment...'} Please wait.
            </Typography>
          </Box>
        )}
        <Button onClick={onClose} variant="outlined" disabled={processing || loading}>
          Cancel
        </Button>
        <Button
          onClick={handleQuickPayment}
          variant="contained"
          disabled={selectedMembers.length === 0 || loading || processing}
          startIcon={(loading || processing) ? null : <CheckCircle />}
          sx={{ flex: { xs: 1, sm: '0 0 auto' }, whiteSpace: 'nowrap' }}
        >
          {recordLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickCollection;